import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, ChevronLeft, ChevronRight, Heart, Monitor, CalendarDays, Star, Crown } from 'lucide-react';

const WHATSAPP_NUMBER = '5535988366028';
const getWhatsAppUrl = (planName) => {
  const msg = encodeURIComponent(`Olá Thiago, tudo bem? Vim pelo seu site e fiquei interessado(a) no plano ${planName}. Gostaria de entender melhor como funciona o acompanhamento, qual a frequência das consultas e os valores. Poderia me passar mais detalhes? Desde já, agradeço!`);
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`;
};

const allPlans = [
  {
    id: 'mensal',
    name: 'Mensal',
    subtitle: 'Renovação automática',
    icon: CalendarDays,
    features: [
      '1 consulta inicial completa',
      'Plano nutricional personalizado',
      'Acompanhamento mensal',
      'Acesso ao App personalizado',
      'Suporte via WhatsApp'
    ],
    highlighted: false,
    themed: false
  },
  {
    id: 'casal',
    name: 'Casal',
    subtitle: 'Cuidem-se juntos',
    catchPhrase: 'Juntos, a mudança é mais leve e os resultados, mais duradouros.',
    icon: Heart,
    features: [
      'Consulta inicial para o casal',
      '2 planos nutricionais personalizados',
      'Acompanhamento mensal conjunto',
      'Acesso ao App personalizado',
      'Suporte via WhatsApp para ambos'
    ],
    highlighted: false,
    themed: true
  },
  {
    id: 'online',
    name: 'Online',
    subtitle: 'De qualquer lugar',
    icon: Monitor,
    features: [
      'Consulta por videochamada',
      'Plano nutricional personalizado',
      'Acompanhamento mensal online',
      'Avaliação corporal por IA por meio de fotos',
      'Acesso ao App personalizado',
      'Suporte via WhatsApp'
    ],
    highlighted: false,
    themed: false
  },
  {
    id: 'trimestral',
    name: 'Trimestral',
    subtitle: 'Melhor custo-benefício',
    badge: 'MAIS POPULAR',
    icon: Star,
    features: [
      '3 consultas',
      '3 meses de acompanhamento',
      'Acesso ao App personalizado',
      'Suporte priorizado',
      'Ajustes do plano conforme necessário'
    ],
    highlighted: true,
    themed: false
  },
  {
    id: 'semestral',
    name: 'Semestral',
    subtitle: 'Máxima dedicação',
    icon: Crown,
    features: [
      '6 consultas',
      '6 meses de acompanhamento',
      'Acesso ao App personalizado',
      'Suporte premium 24h',
      'Ajustes do plano conforme necessário'
    ],
    highlighted: false,
    themed: false
  }
];

const PlanCard = ({ plan }) => {
  const whatsUrl = getWhatsAppUrl(plan.name);
  const Icon = plan.icon;

  return (
    <div
      data-testid={`pricing-plan-${plan.id}`}
      className={`relative rounded-2xl p-6 md:p-7 flex flex-col h-full transition-all duration-300 hover:scale-[1.03] hover:-translate-y-2 cursor-pointer mt-6 ${
        plan.highlighted
          ? 'bg-brand-green text-white shadow-[0_20px_60px_rgba(17,131,15,0.25)]'
          : plan.themed
          ? 'bg-white border-2 border-brand-green/20 shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:shadow-[0_16px_50px_rgba(0,0,0,0.12)]'
          : 'bg-white border border-gray-200 shadow-[0_8px_30px_rgba(0,0,0,0.06)] hover:shadow-[0_16px_50px_rgba(0,0,0,0.12)]'
      }`}
    >
      {plan.badge && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
          <span className="bg-brand-green text-white px-5 py-1.5 rounded-full text-xs font-bold tracking-wider whitespace-nowrap">
            {plan.badge}
          </span>
        </div>
      )}

      <div className="text-center mb-4">
        {/* Icon above title for ALL plans */}
        {Icon && (
          <div className="flex justify-center mb-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              plan.highlighted ? 'bg-white/20' : plan.themed ? 'bg-red-50' : 'bg-brand-green/10'
            }`}>
              {plan.themed ? (
                <Heart className="w-6 h-6 text-red-400 fill-red-400" />
              ) : (
                <Icon className={`w-6 h-6 ${plan.highlighted ? 'text-white' : 'text-brand-green'}`} />
              )}
            </div>
          </div>
        )}

        <h3 className={`text-2xl md:text-3xl font-bold mb-1 ${
          plan.highlighted ? 'text-white' : 'text-[#1A1A1A]'
        }`} style={{ fontFamily: 'Playfair Display, serif' }}>
          {plan.name}
        </h3>
        <p className={`text-sm ${plan.highlighted ? 'text-white/70' : 'text-gray-500'}`}>
          {plan.subtitle}
        </p>

        {plan.catchPhrase && (
          <p className={`text-xs mt-3 italic leading-relaxed ${plan.highlighted ? 'text-white/80' : 'text-brand-green'}`}>
            {plan.catchPhrase}
          </p>
        )}
      </div>

      <div className={`w-full h-px mb-4 ${plan.highlighted ? 'bg-white/20' : 'bg-gray-200'}`} />

      <ul className="space-y-3 mb-6 flex-grow flex flex-col justify-center px-4">
        {plan.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2.5">
            <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.highlighted ? 'text-white' : 'text-brand-green'}`} />
            <span className={`text-sm leading-snug ${plan.highlighted ? 'text-white/90' : 'text-gray-600'}`}>
              {feature}
            </span>
          </li>
        ))}
      </ul>

      <a
        href={whatsUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`pricing-cta-${plan.id}`}
        className={`w-full py-3.5 rounded-full font-bold text-sm uppercase tracking-wider transition-all duration-300 text-center block ${
          plan.highlighted
            ? 'bg-white text-brand-green hover:bg-gray-100 shadow-lg hover:shadow-xl'
            : 'bg-brand-green text-white hover:bg-[#0d6a0c] shadow-lg hover:shadow-xl'
        }`}
      >
        Escolher Plano
      </a>
    </div>
  );
};

const Pricing = () => {
  const [offset, setOffset] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(() => {
    if (typeof window !== 'undefined') return window.innerWidth < 768 ? 1 : 3;
    return 3;
  });

  useEffect(() => {
    const updateView = () => {
      const newCards = window.innerWidth < 768 ? 1 : 3;
      setCardsPerView(newCards);
      setOffset(0);
    };
    window.addEventListener('resize', updateView);
    return () => window.removeEventListener('resize', updateView);
  }, []);

  const maxOffset = allPlans.length - cardsPerView;

  const next = () => setOffset((prev) => Math.min(prev + 1, maxOffset));
  const prev = () => setOffset((prev) => Math.max(prev - 1, 0));

  const cardWidthPercent = 100 / cardsPerView;
  const gapAdjust = cardsPerView === 1 ? 0 : cardWidthPercent * 0.05;

  return (
    <section className="relative overflow-hidden" id="planos">
      {/* Green-tinted frosted/cloud background */}
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(135deg, #b8e6b5 0%, #8fd4a0 25%, #a3d9b3 50%, #7cc99a 75%, #9ad4ab 100%)'
      }} />

      {/* Cloud-like blurred shapes */}
      <div className="absolute top-[-10%] left-[-5%] w-[45%] h-[60%] rounded-full opacity-60" style={{
        background: 'radial-gradient(ellipse, rgba(255,255,255,0.7) 0%, transparent 70%)',
        filter: 'blur(60px)'
      }} />
      <div className="absolute bottom-[-15%] right-[-10%] w-[50%] h-[65%] rounded-full opacity-50" style={{
        background: 'radial-gradient(ellipse, rgba(255,255,255,0.6) 0%, transparent 70%)',
        filter: 'blur(70px)'
      }} />
      <div className="absolute top-[30%] right-[10%] w-[35%] h-[40%] rounded-full opacity-40" style={{
        background: 'radial-gradient(ellipse, rgba(255,255,255,0.5) 0%, transparent 65%)',
        filter: 'blur(50px)'
      }} />
      <div className="absolute bottom-[20%] left-[15%] w-[30%] h-[35%] rounded-full opacity-35" style={{
        background: 'radial-gradient(ellipse, rgba(200,240,210,0.6) 0%, transparent 70%)',
        filter: 'blur(55px)'
      }} />

      {/* Subtle bokeh dots */}
      <div className="absolute top-[15%] left-[20%] w-16 h-16 rounded-full opacity-20" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%)',
        filter: 'blur(8px)'
      }} />
      <div className="absolute top-[60%] right-[25%] w-20 h-20 rounded-full opacity-15" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.7) 0%, transparent 70%)',
        filter: 'blur(10px)'
      }} />
      <div className="absolute bottom-[35%] left-[60%] w-12 h-12 rounded-full opacity-25" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.9) 0%, transparent 70%)',
        filter: 'blur(6px)'
      }} />

      <div className="relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-4"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Nossos <span className="text-brand-green">Planos</span>
            </h2>
            <p className="text-base md:text-lg text-gray-700 max-w-2xl mx-auto">
              Invista na sua saúde com um dos nossos planos de acompanhamento nutricional
            </p>
          </motion.div>

          {/* Carousel */}
          <div className="flex items-center gap-2 md:gap-6 mt-12">
            <button
              onClick={prev}
              disabled={offset === 0}
              className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/80 backdrop-blur-sm shadow-md border border-white/50 flex items-center justify-center hover:bg-brand-green hover:text-white hover:border-brand-green transition-all text-[#1A1A1A] disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="pricing-prev"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
            </button>

            <div className="flex-grow overflow-hidden">
              <motion.div
                className="flex"
                animate={{ x: `-${offset * (cardWidthPercent + gapAdjust)}%` }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              >
                {allPlans.map((plan) => (
                  <div key={plan.id} className="flex-shrink-0 px-2 md:px-3" style={{ width: `${cardWidthPercent}%` }}>
                    <PlanCard plan={plan} />
                  </div>
                ))}
              </motion.div>
            </div>

            <button
              onClick={next}
              disabled={offset >= maxOffset}
              className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/80 backdrop-blur-sm shadow-md border border-white/50 flex items-center justify-center hover:bg-brand-green hover:text-white hover:border-brand-green transition-all text-[#1A1A1A] disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="pricing-next"
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-10">
            {Array.from({ length: maxOffset + 1 }).map((_, idx) => (
              <button
                key={idx}
                onClick={() => setOffset(idx)}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  idx === offset ? 'bg-brand-green w-8' : 'bg-white/50 w-2.5 hover:bg-white/70'
                }`}
                data-testid={`pricing-dot-${idx}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
